import { useState, type ReactNode } from 'react';
import { Field, Input, Textarea } from '@bb/ui';
import type { CampaignView, TestSendChannel } from '../../../api/client.ts';
import { CardContentFields } from '../../canvas/editor/steps/MessageStepPanel.tsx';
import { PushComposer } from '../../composer/push/PushComposer.tsx';
import { MessageTestPanel } from '../../composer/shared/MessageTestPanel.tsx';
import { detectMode, type PushComposerMode, type PushComposerValue } from '../../composer/push/pushDraft.ts';
import { WebhookComposer } from '../../composer/webhook/WebhookComposer.tsx';
import { ChannelsPicker } from '../compose/ChannelsPicker.tsx';
import { addChannel, createMessagesDraft, insertVariantTemplate, removeChannel, setVariantContent, type ChannelContent, type DeliveryChannel, type MessagesDraft } from '../compose/messagesDraft.ts';
import { TemplateActions } from '../../templates/TemplatePickerDialog.tsx';
import { templateChannelOf, templateContentOf } from '../../templates/templateContent.ts';
import { VariantsEditor } from '../compose/VariantsEditor.tsx';
import type { CampaignWizardDraft, WizardIssue } from './steps.ts';

export interface ComposeStepProps {
  environmentId: string;
  campaign: CampaignView;
  draft: CampaignWizardDraft;
  onChange: (next: CampaignWizardDraft) => void;
  issues: WizardIssue[];
  readOnly: boolean;
  /** The campaign has been launched: a card's creation mode is fixed. */
  launched: boolean;
  /** The last failed save: a 422 `translation_incomplete` is shown in the push locale tabs. */
  saveError?: unknown;
}

const PUSH: readonly DeliveryChannel[] = ['push_android', 'push_ios'];

/** The push channels of a variant as the push composer edits them. */
function pushValue(messages: MessagesDraft, index: number): PushComposerValue {
  const variant = messages.variants[index]!;
  return Object.fromEntries(PUSH.filter((c) => messages.channels.includes(c)).map((c) => [c, variant.content[c] ?? {}])) as PushComposerValue;
}

/** The push composer's edit of one variant: a platform it switches off or on is a campaign channel (for every variant). */
export function applyPush(messages: MessagesDraft, index: number, next: PushComposerValue): MessagesDraft {
  let out = messages;
  for (const channel of PUSH) {
    const content = next[channel as keyof PushComposerValue] as ChannelContent | undefined;
    if (content) {
      if (!out.channels.includes(channel)) out = addChannel(out, channel);
      out = setVariantContent(out, index, channel, content);
    } else if (out.channels.includes(channel)) out = removeChannel(out, channel);
  }
  return out;
}

/**
 * Compose (BB-97 over BB-98): the name and goal, the channels, the variants with their shares and control, and each
 * variant's content through the channel composers — push (BB-73), Content Card (BB-62…BB-65) and webhook (BB-142).
 */
export function ComposeStep({ environmentId, campaign, draft, onChange, issues, readOnly, launched, saveError }: ComposeStepProps) {
  const messages = Array.isArray(draft.content.messages) ? createMessagesDraft() : draft.content.messages;
  const [modes, setModes] = useState<Record<string, PushComposerMode>>({});
  const [templateNotice, setTemplateNotice] = useState<string | null>(null);
  const setMessages = (next: MessagesDraft) => onChange({ ...draft, content: { ...draft.content, messages: next.channels.length ? next : [] } });
  const nameError = issues.find((i) => i.path === 'name')?.message;

  // Template library (BB-56): insert a copy into the variant, or save the variant's channel content as a template.
  const templates = (variantApiId: string, channel: DeliveryChannel, locked: boolean): ReactNode => {
    const templateChannel = templateChannelOf(channel);
    const index = messages.variants.findIndex((v) => v.apiId === variantApiId);
    const variant = messages.variants[index];
    if (!templateChannel || !variant) return null;
    const source = variant.sourceTemplates?.[channel];
    return (
      <>
        <TemplateActions
          environmentId={environmentId}
          channel={templateChannel}
          content={templateContentOf(templateChannel, variant.content)}
          readOnly={locked}
          onInsert={(instance) => {
            const next = insertVariantTemplate(messages, index, instance);
            if (next) {
              // The push composer re-detects shared / separate from the inserted content.
              setModes(({ [variantApiId]: _old, ...rest }) => rest);
              setMessages(next);
            }
            setTemplateNotice(next ? `Inserted «${instance.name}» v${instance.source.version} into ${variant.name}.` : `«${instance.name}» has no content for this campaign's platforms.`);
          }}
        />
        {source && <p className="hint">Created from a template (v{source.version}); editing it here does not change the template.</p>}
      </>
    );
  };

  // Test send and preview links of the variant's content as edited (BB-59).
  const test = (variantApiId: string, channels: TestSendChannel[], contentOf: (c: TestSendChannel) => ChannelContent | undefined) => (
    <MessageTestPanel environmentId={environmentId} channels={channels} contentOf={contentOf} campaignId={campaign.id} variantApiId={variantApiId} />
  );
  const renderContent = (variantApiId: string, channel: DeliveryChannel, content: ChannelContent, setContent: (next: ChannelContent) => void, locked: boolean) => {
    const index = messages.variants.findIndex((v) => v.apiId === variantApiId);
    if (channel === 'push_android' || channel === 'push_ios') {
      // One push composer per variant covers both platforms.
      if (channel === 'push_ios' && messages.channels.includes('push_android')) return null;
      const value = pushValue(messages, index);
      const mode = modes[variantApiId] ?? detectMode(value);
      return (
        <>
          <PushComposer
          environmentId={environmentId}
          campaignId={campaign.id}
          category={campaign.category}
          mode={mode}
          onModeChange={(m) => setModes((all) => ({ ...all, [variantApiId]: m }))}
          value={value}
          onChange={(next) => setMessages(applyPush(messages, index, next))}
          readOnly={locked}
          readOnlyNotice="You can view this campaign but not change it."
          saveError={saveError}
          />
          {templates(variantApiId, channel, locked)}
          {test(variantApiId, (['push_android', 'push_ios'] as const).filter((c) => messages.channels.includes(c)), (c) => messages.variants[index]?.content[c])}
        </>
      );
    }
    if (channel === 'content_card')
      return (
        <>
          <CardContentFields environmentId={environmentId} content={content} readOnly={locked} launched={launched} durationLabel="A set time after the card is created" onChange={setContent} />
          {templates(variantApiId, channel, locked)}
          {test(variantApiId, ['content_card'], () => content)}
        </>
      );
    if (channel === 'webhook')
      return (
        <>
          <WebhookComposer environmentId={environmentId} campaignId={campaign.id} value={content} readOnly={locked} onChange={setContent} />
          {templates(variantApiId, channel, locked)}
          {test(variantApiId, ['webhook'], () => content)}
        </>
      );
    if (channel === 'push_web') return <p className="notice notice--neutral">Web push has no composer yet: its content is kept as it is.</p>;
    return (
      <>
        <p className="notice notice--warning">The in-app message composer for campaigns comes with BB-85: this campaign’s in-app content cannot be edited here yet, but an in-app template can be inserted.</p>
        {templates(variantApiId, channel, locked)}
      </>
    );
  };

  return (
    <section aria-label="Compose" className="canvas-wizard__panel canvas-wizard__panel--wide">
      <h2>Compose</h2>
      <Field label="Campaign name" error={nameError}>
        <Input value={draft.name} maxLength={200} disabled={readOnly} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
      </Field>
      <Field label="Goal" hint="What the campaign should achieve, e.g. «first deposit within 3 days». Its primary conversion event is set in Assign conversions.">
        <Textarea value={draft.description} rows={2} maxLength={1000} disabled={readOnly} onChange={(e) => onChange({ ...draft, description: e.target.value })} />
      </Field>
      <ChannelsPicker value={messages} onChange={setMessages} readOnly={readOnly} />
      {messages.channels.length > 0 && <VariantsEditor value={messages} onChange={setMessages} readOnly={readOnly} renderContent={renderContent} />}
      {messages.channels.length === 0 && <p className="hint">Choose at least one channel to write the message.</p>}
      {templateNotice && <p role="status">{templateNotice}</p>}
      {issues
        .filter((i) => i.path !== 'name' && i.severity === 'error' && i.path.split('.').length <= 2)
        .map((i) => (
          <p key={`${i.path}-${i.message}`} className="bb-field__error" role="alert">
            {i.message}
          </p>
        ))}
    </section>
  );
}

